import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface PrivacyPolicyModalProps {
  isVisible: boolean;
  onClose: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isVisible, onClose }) => {
  return createPortal(
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 10001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10002,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1rem'
            }}
          >
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              padding: '1.5rem',
              maxWidth: '48rem',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem' }}>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#111827', margin: 0 }}>
                    Privacy Policy
                  </h2>
                  <button
                    onClick={onClose}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '0.25rem'
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Content */}
                <div style={{ fontSize: '0.875rem', lineHeight: '1.6', color: '#374151' }}>
                  <p style={{ marginBottom: '1rem' }}>
                    <strong>Last updated:</strong> {new Date().toLocaleDateString()}
                  </p>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      1. Information We Collect
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      We collect information you provide directly to us, such as when you create an account, 
                      participate in games, or contact us for support.
                    </p>
                    <ul style={{ marginLeft: '1.5rem', marginBottom: '0.5rem' }}>
                      <li>Account information (email, username)</li>
                      <li>Game statistics and performance data</li>
                      <li>Communication preferences</li>
                      <li>Technical information about your device</li>
                    </ul>
                  </section>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      2. How We Use Your Information
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      We use the information we collect to:
                    </p>
                    <ul style={{ marginLeft: '1.5rem', marginBottom: '0.5rem' }}>
                      <li>Provide and improve our gaming services</li>
                      <li>Personalize your gaming experience</li>
                      <li>Analyze game performance and user behavior</li>
                      <li>Communicate with you about updates and features</li>
                      <li>Ensure security and prevent fraud</li>
                    </ul>
                  </section>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      3. Cookie Policy
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      We use cookies and similar technologies to enhance your experience:
                    </p>
                    <ul style={{ marginLeft: '1.5rem', marginBottom: '0.5rem' }}>
                      <li><strong>Necessary cookies:</strong> Essential for website functionality</li>
                      <li><strong>Analytics cookies:</strong> Help us understand how you use our site</li>
                      <li><strong>Advertising cookies:</strong> Make ads more relevant to you</li>
                      <li><strong>Personalization cookies:</strong> Remember your preferences</li>
                    </ul>
                  </section>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      4. Information Sharing
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      We do not sell your personal information. We may share your information with:
                    </p>
                    <ul style={{ marginLeft: '1.5rem', marginBottom: '0.5rem' }}>
                      <li>Service providers who help us operate our services</li>
                      <li>Legal authorities when required by law</li>
                      <li>Other users (only with your explicit consent)</li>
                    </ul>
                  </section>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      5. Your Rights
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      You have the right to:
                    </p>
                    <ul style={{ marginLeft: '1.5rem', marginBottom: '0.5rem' }}>
                      <li>Access your personal information</li>
                      <li>Correct inaccurate information</li>
                      <li>Request deletion of your data</li>
                      <li>Withdraw consent for data processing</li>
                      <li>Export your data</li>
                      <li>Lodge a complaint with supervisory authorities</li>
                    </ul>
                  </section>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      6. Data Security
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      We implement appropriate security measures to protect your personal information 
                      against unauthorized access, alteration, disclosure, or destruction.
                    </p>
                  </section>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      7. Contact Us
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      If you have any questions about this Privacy Policy, please contact us:
                    </p>
                    <ul style={{ marginLeft: '1.5rem', marginBottom: '0.5rem' }}>
                      <li><strong>Email:</strong> privacy@triple-freezer.com</li>
                      <li><strong>Website:</strong> triple-freezer.com</li>
                    </ul>
                  </section>
                </div>

                {/* Footer */}
                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', textAlign: 'center' }}>
                  <button
                    onClick={onClose}
                    style={{
                      padding: '0.5rem 1.5rem',
                      backgroundColor: '#2563eb',
                      color: 'white',
                      borderRadius: '0.5rem',
                      border: 'none',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default PrivacyPolicyModal; 